import { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from "@mui/material";
import moment from "moment";
import {
  getSubmissions,
  removeSubmission,
  submitAssignmentReport,
} from "../../../store/rbm/actions";
import ConfirmationDialog from "./ConfirmationDialog";
import PreviewPdfUrl from "../../common/components/PreviewPdfUrl";
import loadingGif from "../../assets/loading.gif";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonIcon from "@mui/icons-material/Person";
import CustomProgressLine from "./CustomProgressLine";
import {
  computeMonthlyStatuses,
  getMeasurementBounds,
  monthsOfQuarter,
} from "./rbmCalculations";
import ConfirmSubmission from "./ConfirmSubmission";
import PercentageSelect from "../../common/components/PercentageSelect";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";

export const formatSubmission = (submission) => {
  return {
    createdOn: submission.createdOn,
    lastName: submission.evaluation
      ? submission.evaluation.evaluatedBy.lastName
      : null,
    firstName: submission.evaluation
      ? submission.evaluation.evaluatedBy.firstName
      : null,
    reportedMessage: submission.message,
    status: submission.evaluation ? "scored" : "reported",
    submissionId: submission.id,
    targetShare: submission.thisTarget,
    achievedTarget: submission.achievedTarget,
    measurementUnit: submission.measurementUnit,
    completed: submission.completed,
    docReferenceId: submission.docReferenceId,
    fileName: submission.fileName,
    evaluation: submission.evaluation || null,
  };
};

const ReportAssignment = ({
  assignment,
  totalWeight,

  loading,
  submitAssignmentReport,
  removeSubmission,
  getSubmissions,

  showDialog,
  setShowDialog,
  index,
  no,
  submissions,
  subordinatePendingReports,

  user,
}) => {
  const { RBM_fiscalYear } = user;

  const [reportFormData, setReportFormData] = useState({
    achievedTarget: "",
    completed: "",
    employeeMessage: "",
    file: null,
  });

  const [errors, setErrors] = useState({
    hasError: false,
    achievedTargetHasError: false,
    messageHasError: false,
    fileHasError: false,
    fileErrorMessge: "",
    achievedTargetErrorMessage: "",
    messageErrorMessge: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Month selection (quarter chips)
  const [selectedMonth, setSelectedMonth] = useState(null); // { isoMonth, label, m }
  const fyStartYear = moment(RBM_fiscalYear.startOn).year();
  const quarterMonths = monthsOfQuarter(fyStartYear, assignment.quarterId);
  const monthStatuses = computeMonthlyStatuses({
    months: quarterMonths,
    submissions,
  });

  // Preview
  const [previewOpen, setPreviewOpen] = useState(false);

  // Special indicators require Excel
  const requireSpecialReport =
    !!assignment.isSpecial && assignment.itemTypeId === 2;

  // Measurement bounds
  const code = assignment.measurementUnitCode || "SUM";
  const bounds = getMeasurementBounds({
    code,
    target: assignment.targetShare,
  });

  // Keep your canReport rule (activities bypass)
  const canReport =
    !subordinatePendingReports?.allReports || assignment.itemTypeId === 3;

  // --- Validation ---
  const formValidator = () => {
    const error = {
      hasError: false,
      achievedTargetHasError: false,
      messageHasError: false,
      fileHasError: false,
      fileErrorMessge: "",
      achievedTargetErrorMessage: "",
      messageErrorMessge: "",
    };

    // Month required when monthly reporting
    if (assignment.reportingMode === "monthly" && !selectedMonth) {
      error.messageHasError = true;
      error.messageErrorMessge = "Select the month you are reporting for.";
      error.hasError = true;
    }

    // Completion required when target is 0 (boolean/scale path)
    if (assignment.targetShare === 0 && reportFormData.completed === "") {
      error.achievedTargetHasError = true;
      error.achievedTargetErrorMessage =
        assignment.itemTypeId === 1
          ? "Please, select the level of output completion."
          : `Please, specify if ${
              assignment.itemTypeId === 2 ? "indicator" : "activity"
            } was completed or not`;
      error.hasError = true;
    }

    // Numeric required when target > 0
    if (assignment.targetShare > 0 && reportFormData.achievedTarget === "") {
      error.achievedTargetHasError = true;
      error.achievedTargetErrorMessage = `Achieved target is required`;
      error.hasError = true;
    }

    // Message required
    if (!reportFormData.employeeMessage) {
      error.messageHasError = true;
      error.messageErrorMessge = `Please, fill the message field`;
      error.hasError = true;
    }

    // File type/size rules
    if (reportFormData.file) {
      if (requireSpecialReport) {
        const okExcel = [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/vnd.ms-excel",
        ];
        if (!okExcel.includes(reportFormData.file.type)) {
          error.fileHasError = true;
          error.fileErrorMessge =
            "Only Excel files are allowed for special reports";
          error.hasError = true;
        }
      } else {
        if (reportFormData.file.type !== "application/pdf") {
          error.fileHasError = true;
          error.fileErrorMessge = "Only PDF files are allowed";
          error.hasError = true;
        }
      }
      // 1MB size cap
      if (reportFormData.file.size > 1048576) {
        error.fileHasError = true;
        error.fileErrorMessge = "File size cannot exceed 1MB.";
        error.hasError = true;
      }
    }

    // Special report requires a file
    if (requireSpecialReport && !reportFormData.file) {
      error.fileHasError = true;
      error.fileErrorMessge =
        "Supporting document is required for this indicator!";
      error.hasError = true;
    }

    // Numeric bounds validation (when applicable)
    const numericFilled =
      assignment.targetShare > 0 && reportFormData.achievedTarget !== "";

    if (numericFilled) {
      const v = Number(reportFormData.achievedTarget);
      if (!Number.isFinite(v)) {
        error.achievedTargetHasError = true;
        error.achievedTargetErrorMessage = `Enter a valid number.`;
        error.hasError = true;
      } else {
        if (bounds.forceEquals && v !== bounds.max) {
          error.achievedTargetHasError = true;
          error.achievedTargetErrorMessage = `Value must equal ${bounds.max}.`;
          error.hasError = true;
        }
        const belowMin = bounds.min != null && v < bounds.min;
        const aboveMax = bounds.max != null && v > bounds.max;
        if (belowMin || aboveMax) {
          error.achievedTargetHasError = true;
          const lo = bounds.min ?? "…";
          const hi = bounds.max ?? "…";
          error.achievedTargetErrorMessage = `Allowed range is ${lo} – ${hi}.`;
          error.hasError = true;
        }
      }
    }

    setErrors(error);
    return error.hasError;
  };

  // --- Effects ---
  useEffect(() => {
    if (showDialog)
      getSubmissions(
        {
          assignmentId: assignment.assignmentId,
          quarterId: assignment.quarterId,
          itemTypeId: assignment.itemTypeId,
        },
        setIsLoading
      );
  }, [showDialog]);

  // --- Submit handlers ---
  const onTrySubmit = () => {
    if (formValidator()) return;
    setPreviewOpen(true);
  };

  const onSubmitReport = () => {
    if (!formValidator()) {
      const reportData = new FormData();
      reportData.append("itemId", assignment.id);
      reportData.append("itemTypeId", assignment.itemTypeId);
      reportData.append("contractId", assignment.contractId);
      reportData.append("assignmentId", assignment.assignmentId);
      reportData.append("achievedTarget", reportFormData.achievedTarget);
      reportData.append("employeeMessage", reportFormData.employeeMessage);
      reportData.append("completed", reportFormData.completed);
      reportData.append("file", reportFormData.file);

      // include month for monthly reporting across ALL item types
      if (assignment.reportingMode === "monthly" && selectedMonth?.m) {
        reportData.append("month", selectedMonth.m);
      }

      setPreviewOpen(false);

      submitAssignmentReport(reportData, setIsSubmitting, () =>
        setReportFormData({
          achievedTarget: "",
          completed: "",
          employeeMessage: "",
          file: null,
        })
      );
    }
  };

  return (
    <>
      <Dialog
        aria-labelledby="customized-dialog-title"
        open={showDialog}
        onClose={() => setShowDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle className="text-primary">
          <span>Assignment reports</span>
          <IconButton
            aria-label="close"
            onClick={() => setShowDialog(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <span className="material-icons">close</span>
          </IconButton>
          <div className="mb-1 text-black" style={{ fontSize: "11px" }}>
            <span className="text-uppercase d-inline-flex align-items-center">
              <strong className="font-weight-light">
                {(index || no) + 1}:{assignment.itemTypeName}
              </strong>
              <ArrowRightIcon className="text-primary" />
              {assignment.name}{" "}
            </span>
            <div>
              <span className="text-info">
                Target share: {assignment.targetShare}{" "}
                {assignment.measurementUnit}
              </span>{" "}
              | Planned from:{" "}
              <span className="text-info">
                {moment(assignment.startOn).format("ll")}
              </span>{" "}
              To:{" "}
              <span className="text-info">
                {moment(assignment.endOn).format("ll")}
              </span>
              <strong
                style={{ position: "absolute", right: 20 }}
                className="badge-info text-uppercase px-1"
              >
                quarter {assignment.quarterId}
              </strong>{" "}
            </div>
          </div>

          {!canReport && (
            <Alert severity="warning" className="mt-2" icon={false}>
              <>
                Please note that you cannot submit your report if there are
                pending evaluations for reports submitted by your subordinates.
                Ensure that{" "}
                {subordinatePendingReports?.forActiveReports > 0 && (
                  <>
                    <b>{subordinatePendingReports.forActiveReports}</b> report
                    {subordinatePendingReports.forActiveReports > 1
                      ? "s"
                      : ""}{" "}
                    for active employee(s)
                  </>
                )}
                {subordinatePendingReports?.forActiveReports > 0 &&
                  subordinatePendingReports?.forExitedReports > 0 &&
                  " and "}
                {subordinatePendingReports?.forExitedReports > 0 && (
                  <>
                    <b>{subordinatePendingReports.forExitedReports}</b> report
                    {subordinatePendingReports.forExitedReports > 1
                      ? "s"
                      : ""}{" "}
                    for exited employee(s)
                  </>
                )}{" "}
                {subordinatePendingReports?.allReports > 1 ? "are" : "is"}{" "}
                assessed before proceeding with your submission.
              </>
            </Alert>
          )}
        </DialogTitle>

        {isSubmitting && <LinearProgress />}

        <DialogContent dividers style={{ minHeight: "52vh" }}>
          {!!isLoading && (
            <div className="text-center mt-5">
              <img src={loadingGif} height="128" alt="loading" />
            </div>
          )}

          {!isLoading && (
            <>
              {(!!assignment.employeeTimePenalities ||
                !!assignment.employeeQualityPenalities) && (
                <div className="d-flex align-items-center justify-content-end">
                  {!!assignment.employeeTimePenalities && (
                    <span
                      className={`${
                        !!assignment.employeeQualityPenalities ? "mx-2" : ""
                      } badge badge-warning text-left text-uppercase`}
                      style={{ minWidth: "125px" }}
                    >
                      <span
                        className="badge badge-light bg-warning text-light"
                        style={{ fontSize: "12px" }}
                      >
                        {assignment.employeeTimePenalities}
                      </span>{" "}
                      Time penalty
                    </span>
                  )}
                  {!!assignment.employeeQualityPenalities && (
                    <span
                      className="badge badge-danger text-left text-uppercase"
                      style={{ minWidth: "125px" }}
                    >
                      <span
                        className="badge badge-light bg-danger text-light"
                        style={{ fontSize: "12px" }}
                      >
                        {assignment.employeeQualityPenalities}
                      </span>{" "}
                      Quality penalty
                    </span>
                  )}
                </div>
              )}

              {!!submissions.length &&
                submissions.map((submission) => (
                  <SubmissionCard
                    key={submission.id}
                    submission={submission}
                    removeSubmission={removeSubmission}
                    assignment={assignment}
                  />
                ))}

              {(!submissions.length ||
                (!!submissions &&
                  !!submissions[submissions.length - 1]?.evaluation &&
                  !submissions[submissions.length - 1]?.evaluation
                    ?.accepted)) && (
                <div className="border p-4 rounded">
                  {/* Month chips for monthly reporting */}
                  {assignment.reportingMode === "monthly" && (
                    <div className="d-flex align-items-center mb-2">
                      <small className="text-muted mr-2">Quarter months:</small>
                      <div className="d-flex flex-wrap">
                        {monthStatuses.map((m) => (
                          <Chip
                            key={m.isoMonth}
                            label={m.label}
                            onClick={() => {
                              setSelectedMonth(m);
                              setErrors((e) => ({
                                ...e,
                                messageHasError: false,
                                messageErrorMessge: "",
                              }));
                            }}
                            className="mr-1 mb-1"
                            color={
                              m.status === "reported"
                                ? "success"
                                : m.status === "missed"
                                ? "warning"
                                : "default"
                            }
                            icon={
                              m.status === "reported" ? (
                                <CheckCircleIcon />
                              ) : m.status === "missed" ? (
                                <CancelIcon />
                              ) : (
                                <HourglassEmptyIcon />
                              )
                            }
                            variant={
                              selectedMonth?.isoMonth === m.isoMonth
                                ? "filled"
                                : "outlined"
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {assignment.reportingMode === "monthly" && !selectedMonth && (
                    <Alert severity="info" className="mb-2">
                      Select a month to report.
                    </Alert>
                  )}

                  {/* Prompt */}
                  {assignment.targetShare > 0 && assignment.itemTypeId !== 1 ? (
                    <div className="mb-2" style={{ fontSize: "13px" }}>
                      From{" "}
                      <span className="font-weight-bold text-info">
                        {assignment.targetShare} {assignment.measurementUnit}
                      </span>{" "}
                      target, how many did you achieve?
                    </div>
                  ) : assignment.itemTypeId !== 1 ? (
                    <div>
                      Is this{" "}
                      <span className="badge-info px-1">
                        {assignment.itemTypeName.toLowerCase()}
                      </span>{" "}
                      completed?
                    </div>
                  ) : (
                    <small className="mb-2">
                      At which level the annual target of this
                      <strong className="text-primary px-1">
                        {assignment.itemTypeName.toLowerCase()}
                      </strong>
                      was achieved?
                    </small>
                  )}

                  {/* Numeric path */}
                  {assignment.targetShare > 0 && assignment.itemTypeId !== 1 ? (
                    <>
                      <TextField
                        error={errors.achievedTargetHasError}
                        helperText={
                          errors.achievedTargetErrorMessage ||
                          bounds?.hint ||
                          ""
                        }
                        fullWidth
                        size="small"
                        type="number"
                        name="achievedTarget"
                        label="Achieved Target"
                        variant="outlined"
                        placeholder={String(bounds.min ?? 0)}
                        className="mb-3 mt-1"
                        value={reportFormData.achievedTarget}
                        inputProps={{
                          min: bounds.min != null ? bounds.min : undefined,
                          max: bounds.max != null ? bounds.max : undefined,
                          step: "any",
                        }}
                        inputMode="decimal"
                        onChange={(e) => {
                          setErrors((x) => ({
                            ...x,
                            achievedTargetHasError: false,
                            achievedTargetErrorMessage: "",
                          }));
                          setReportFormData((f) => ({
                            ...f,
                            achievedTarget: e.target.value || "",
                          }));
                        }}
                        disabled={
                          !canReport || isLoading || isSubmitting || loading
                        }
                      />

                      <div className="d-flex align-items-center mb-1">
                        <small className="text-muted">
                          {bounds.isLowerBetter
                            ? "Lower is better"
                            : "Higher is better"}
                        </small>
                        {bounds.min != null && (
                          <span className="badge badge-light ml-2">
                            Min: {bounds.min}
                          </span>
                        )}
                        {bounds.max != null && (
                          <span className="badge badge-light ml-1">
                            Max: {bounds.max}
                          </span>
                        )}
                      </div>

                      {/* Contextual zero nudge */}
                      {Number(reportFormData.achievedTarget) === 0 &&
                        reportFormData.achievedTarget !== "" &&
                        !bounds.isLowerBetter &&
                        !errors.achievedTargetHasError && (
                          <div
                            className="alert alert-warning py-0 mt-n3"
                            style={{ borderRadius: 4 }}
                          >
                            <small style={{ fontSize: 12 }}>
                              Heads up: 0 is the lowest value for this measure
                              and will likely score poorly.
                            </small>
                          </div>
                        )}

                      {Number(reportFormData.achievedTarget) === 0 &&
                        bounds.isLowerBetter &&
                        !errors.achievedTargetHasError && (
                          <div
                            className="alert alert-success py-0 mt-n3"
                            style={{ borderRadius: 4 }}
                          >
                            <small style={{ fontSize: 12 }}>
                              0 is best for this measure (lower is better).
                            </small>
                          </div>
                        )}
                    </>
                  ) : assignment.itemTypeId !== 1 ? (
                    // Completed path
                    <>
                      <RadioGroup
                        row
                        aria-labelledby="demo-row-radio-buttons-group-label"
                        name="row-radio-buttons-group"
                        value={reportFormData.completed}
                        onChange={(e, v) => {
                          setErrors((x) => ({
                            ...x,
                            achievedTargetHasError: false,
                            achievedTargetErrorMessage: "",
                          }));
                          setReportFormData((f) => ({ ...f, completed: v }));
                        }}
                        disabled={
                          !canReport || isLoading || isSubmitting || loading
                        }
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>

                      {reportFormData.completed === "no" && (
                        <PercentageSelect
                          title="Progress Made (%)"
                          size="sm"
                          placeholder="Select completion percentage"
                          className="mb-3"
                          excluded={[100]}
                          value={reportFormData.achievedTarget}
                          onChange={(value) =>
                            setReportFormData((f) => ({
                              ...f,
                              achievedTarget: value || "",
                            }))
                          }
                        />
                      )}

                      {reportFormData.completed === "no" &&
                        Number(reportFormData.achievedTarget || 0) === 0 && (
                          <div
                            className="alert alert-warning py-0 mb-3 mt-n3"
                            style={{ borderRadius: "4px" }}
                          >
                            <small style={{ fontSize: "12px" }}>
                              Zero progress will likely score 0%.
                            </small>
                          </div>
                        )}

                      {errors.achievedTargetHasError && (
                        <small className="d-block mt-n3 mb-3 text-danger">
                          <small>{errors.achievedTargetErrorMessage}</small>
                        </small>
                      )}
                    </>
                  ) : (
                    // Output scale path (itemTypeId === 1)
                    <FormControl fullWidth size="small" className="mb-3 mt-2">
                      <InputLabel
                        id="demo-simple-select-label"
                        className={`${
                          errors.achievedTargetHasError ? "text-danger" : ""
                        }`}
                      >
                        Select the level of achievement
                      </InputLabel>
                      <Select
                        error={errors.achievedTargetHasError}
                        size="small"
                        value={reportFormData.achievedTarget}
                        label="Select the level of achievement"
                        onChange={(e) => {
                          setErrors((x) => ({
                            ...x,
                            achievedTargetHasError: false,
                            achievedTargetErrorMessage: "",
                          }));
                          setReportFormData((f) => ({
                            ...f,
                            achievedTarget: e.target.value,
                          }));
                        }}
                        disabled={
                          !canReport || isLoading || isSubmitting || loading
                        }
                      >
                        <MenuItem value={100}>
                          1:Beyond expected{" "}
                          <small className="ml-1 badge-success px-1 border rounded">
                            ~100%
                          </small>
                        </MenuItem>
                        <MenuItem value={90}>
                          2:As expected{" "}
                          <small className="ml-1 badge-success px-1 border rounded">
                            ~90%
                          </small>
                        </MenuItem>
                        <MenuItem value={80}>
                          3:Great{" "}
                          <small className="ml-1 badge-info px-1 border rounded">
                            ~80%
                          </small>
                        </MenuItem>
                        <MenuItem value={70}>
                          4:Moderate{" "}
                          <small className="ml-1 badge-info px-1 border rounded">
                            ~70%
                          </small>
                        </MenuItem>
                        <MenuItem value={60}>
                          5:Fairly{" "}
                          <small className="ml-1 badge-warning px-1 border rounded">
                            ~60%
                          </small>
                        </MenuItem>
                        <MenuItem value={50}>
                          6:Few{" "}
                          <small className="ml-1 badge-warning px-1 border rounded">
                            ~50%
                          </small>
                        </MenuItem>
                        <MenuItem value={40}>
                          7:Poor{" "}
                          <small className="ml-1 badge-danger px-1 border rounded">
                            ~40%
                          </small>
                        </MenuItem>
                        <MenuItem value={20}>
                          8:Very poor{" "}
                          <small className="ml-1 badge-danger px-1 border rounded">
                            ~20%
                          </small>
                        </MenuItem>
                        <MenuItem value={0}>
                          9:Nothing done{" "}
                          <small className="ml-1 badge-danger px-1 border rounded">
                            ~00%
                          </small>
                        </MenuItem>
                      </Select>
                      <small
                        className="text-danger ml-2"
                        style={{ fontSize: "12px" }}
                      >
                        {errors.achievedTargetErrorMessage}
                      </small>
                    </FormControl>
                  )}

                  {/* Message */}
                  <TextField
                    error={errors.messageHasError}
                    helperText={errors.messageErrorMessge}
                    fullWidth
                    size="small"
                    multiline
                    rows={4}
                    autoFocus={assignment.targetShare === 0}
                    name="employeeMessage"
                    label="Message"
                    variant="outlined"
                    placeholder="Message"
                    className="mb-3"
                    value={reportFormData.employeeMessage}
                    onChange={(e) => {
                      const employeeMessage = e.target.value;
                      setErrors((x) => ({
                        ...x,
                        messageHasError: false,
                        messageErrorMessge: "",
                      }));
                      setReportFormData((f) => ({ ...f, employeeMessage }));
                    }}
                    disabled={
                      !canReport || isLoading || isSubmitting || loading
                    }
                  />

                  {/* File */}
                  <div>
                    <AttachFileIcon /> Supporting document{" "}
                    <small
                      className={`badge-${
                        requireSpecialReport ? "warning" : "secondary"
                      } px-1 rounded`}
                    >
                      ({requireSpecialReport ? "Required" : "Optional"})
                    </small>{" "}
                    <small
                      style={{ fontSize: "12px" }}
                      className="font-weight-bold text-danger"
                    >
                      (Only {requireSpecialReport ? "EXCEL" : "PDF"}
                      <span className="badge-danger"> /1MB (max)</span> is
                      allowed)
                    </small>{" "}
                  </div>
                  <div className="custom-file ">
                    <input
                      type="file"
                      className={`custom-file-input ${
                        errors.fileHasError ? "is-invalid" : ""
                      }`}
                      id="supportingDocument"
                      accept={
                        requireSpecialReport ? ".xlsx,.xls" : "application/pdf"
                      }
                      onChange={(e) => {
                        const file = e.target.files[0];
                        setErrors((x) => ({
                          ...x,
                          fileHasError: false,
                          fileErrorMessge: "",
                        }));
                        setReportFormData((f) => ({
                          ...f,
                          file: file || null,
                        }));
                      }}
                      disabled={
                        !canReport || isLoading || isSubmitting || loading
                      }
                    />

                    <label
                      className="custom-file-label"
                      htmlFor="supportingDocument"
                    >
                      {reportFormData.file
                        ? reportFormData.file.name
                        : "Select file..."}
                    </label>
                    {errors.fileHasError && (
                      <small className="invalid-feedback mt-0">
                        <small>{errors.fileErrorMessge}</small>
                      </small>
                    )}
                  </div>

                  {/* Submit */}
                  <div className="d-flex justify-content-center mt-4">
                    <button
                      onClick={onTrySubmit}
                      type="button"
                      className="btn btn-primary text-uppercase  px-5"
                      disabled={
                        !canReport || isLoading || isSubmitting || loading
                      }
                    >
                      {isSubmitting ? "Wait..." : "Submit"}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions className="d-flex justify-content-center py-4" />
      </Dialog>

      {!!previewOpen && (
        <ConfirmSubmission
          submissions={submissions}
          targetShare={assignment.targetShare}
          measurementUnitCode={assignment.measurementUnitCode || "SUM"}
          reportDeadline={assignment.endOn}
          formData={reportFormData}
          activityWeight={assignment.weight}
          totalWeight={totalWeight}
          reportingMode={assignment.reportingMode}
          selectedMonth={selectedMonth}
          measurementUnit={assignment.measurementUnit}
          onClose={() => setPreviewOpen(false)}
          onSubmit={onSubmitReport}
        />
      )}
    </>
  );
};

const mapStateToProps = ({
  loading,
  submissions,
  subordinatePendingReports,
  user,
}) => {
  return {
    loading,
    submissions,
    subordinatePendingReports,
    user,
  };
};
export default connect(mapStateToProps, {
  submitAssignmentReport,
  removeSubmission,
  getSubmissions,
})(ReportAssignment);

const SubmissionCard = ({ submission, removeSubmission, assignment }) => {
  const [confirmRemoveSubmission, setConfirmRemoveSubmission] = useState(false);
  const [viewSupportDoc, setViewSupportDoc] = useState(false);

  return (
    <>
      <div className="my-2 border rounded p-4">
        {/* report content */}
        <div className="row justify-content-start mb-3">
          <div className="col-11 text-secondary">
            <small
              className="d-flex align-items-center"
              style={{ fontSize: "12px" }}
            >
              <span className="d-none text-capitalize mr-1 d-md-inline-flex text-primary align-items-center">
                <PersonIcon />
                {submission.submitedBy?.lastName.toLowerCase()}{" "}
                {submission.submitedBy?.firstName.toLowerCase()}
              </span>{" "}
              {" /"} {moment(submission.createdOn).format("lll")}
            </small>
            <Card
              variant="outlined"
              className="rounded elevated pb-2"
              style={{ backgroundColor: "#f6f8fa" }}
            >
              <CardContent className=" pt-2 pb-2">
                <div>
                  <span className="d-flex align-items-center justify-content-between">
                    {assignment.targetShare > 0 && (
                      <span
                        className="badge badge-light mb-2 text-primary text-uppercase"
                        style={{ backgroundColor: "transparent" }}
                      >
                        {submission.achievedTarget} {assignment.measurementUnit}{" "}
                        reported
                      </span>
                    )}
                    {assignment.targetShare === 0 && (
                      <>
                        {submission.completed && (
                          <span
                            className="badge badge-light text-primary text-uppercase"
                            style={{ backgroundColor: "transparent" }}
                          >
                            Reported as completed
                          </span>
                        )}
                        {!submission.completed && (
                          <span
                            className="badge badge-light text-danger text-uppercase"
                            style={{ backgroundColor: "transparent" }}
                          >
                            Reported as Not completed
                          </span>
                        )}
                      </>
                    )}

                    {!!submission.docReferenceId && (
                      <Button
                        onClick={() => setViewSupportDoc(true)}
                        size="small"
                        className="float-right"
                      >
                        <AttachFileIcon /> Supporting Document <OpenInNewIcon />
                      </Button>
                    )}
                    {!submission.docReferenceId && (
                      <span className="float-right  badge badge-warning mt-2">
                        No file attached
                      </span>
                    )}

                    <span>
                      <span
                        className={`d-inline-flex align-items-center justify-content-center badge text-${
                          submission.statusId === 1
                            ? "secondary"
                            : submission.statusId === 4
                            ? "success"
                            : "danger"
                        }`}
                        style={{ width: "100px" }}
                      >
                        <span
                          style={{ fontSize: "17px" }}
                          className="material-icons"
                        >
                          {submission.statusId === 1 && <>pending_actions</>}
                          {submission.statusId === 4 && <>done_all</>}
                          {submission.statusId === 5 && <>close</>}
                          {submission.statusId === 2 && <>error</>}
                        </span>
                        <span
                          style={{ fontSize: "10px" }}
                          className="text-uppercase"
                        >
                          {submission.statusId !== 1 && (
                            <>{submission?.status?.name}</>
                          )}

                          {submission.statusId === 1 && <>Pending...</>}
                        </span>
                      </span>

                      <IconButton
                        disabled={!!submission.evaluation}
                        onClick={() => {
                          setConfirmRemoveSubmission(true);
                        }}
                        size="small"
                        color="error"
                        className="mr-n2 mt-n2 border bg-light "
                      >
                        <span className="material-icons ">delete_forever</span>
                      </IconButton>
                    </span>
                  </span>
                </div>

                <TextField
                  InputProps={{
                    readOnly: true,
                  }}
                  fullWidth
                  className="mt-2 bg-white"
                  size="small"
                  multiline
                  rows={3}
                  name="name"
                  label="Employee message"
                  variant="outlined"
                  value={submission.employeeMessage}
                />
              </CardContent>
            </Card>
          </div>
          {/* {submission.statusId === 1 && (
          <div className="col-11">
            <div
              className="alert alert-light text-center py-0 mt-n0"
              style={{ borderRadius: "0px" }}
            >
              <small style={{ fontSize: "12px" }}>
                <marquee behavior="scroll" direction="left" scrollamount="4">
                  Penalties will be applied to the supervisor after{" "}
                  <strong>
                    {" "}
                    {submission.dueDays} day{submission.dueDays > 1 ? "s" : ""}
                  </strong>{" "}
                  without evaluation.{" "}
                  <span className="px-1 badge-warning">(Today included)</span>
                </marquee>
              </small>
            </div>
          </div>
        )} */}
        </div>

        {/* evaluation content */}
        {!!submission && !!submission.evaluation && (
          <div className="row justify-content-end mt-2 mb-3">
            <div className="col-11 text-right">
              <small
                className="d-flex align-items-center"
                style={{ fontSize: "12px" }}
              >
                <span className="d-none text-capitalize mr-1 d-md-inline-flex text-primary align-items-center">
                  <PersonIcon />
                  {submission.evaluation.evaluatedBy.lastName.toLowerCase()}{" "}
                  {submission.evaluation.evaluatedBy.firstName.toLowerCase()}
                </span>{" "}
                {" /"} {moment(submission.evaluation.createdOn).format("lll")}
              </small>

              <Card
                variant="outlined"
                className="rounded elevated"
                style={{ backgroundColor: "#f6f8fa" }}
              >
                <CardContent>
                  <div>
                    <div className="pb-2">
                      {submission.evaluation.accepted && (
                        <span className="float-left mt-n2 text-uppercase">
                          {assignment.targetShare > 0 && (
                            <span
                              className="badge badge-light mb-2 text-primary"
                              style={{ backgroundColor: "transparent" }}
                            >
                              {submission.evaluation.achievedTarget}{" "}
                              {assignment.measurementUnit} accepted
                            </span>
                          )}
                          {assignment.targetShare === 0 && (
                            <>
                              {submission.evaluation.completed && (
                                <span
                                  className="badge badge-light text-success"
                                  style={{ backgroundColor: "transparent" }}
                                >
                                  Completed accepted
                                </span>
                              )}
                              {!submission.evaluation.completed && (
                                <span
                                  className="badge badge-light text-danger"
                                  style={{ backgroundColor: "transparent" }}
                                >
                                  Not completed
                                </span>
                              )}
                            </>
                          )}

                          <span
                            className={`badge ml-2 badge-${
                              +submission.evaluation.qualityValue >= 0.95
                                ? "success"
                                : +submission.evaluation.qualityValue >= 0.85
                                ? "primary"
                                : +submission.evaluation.qualityValue >= 0.75
                                ? "info"
                                : +submission.evaluation.qualityValue >= 0.6
                                ? "warning"
                                : "danger"
                            }`}
                          >
                            {+submission.evaluation.qualityValue >= 0.95
                              ? "Excellent"
                              : +submission.evaluation.qualityValue >= 0.85
                              ? "Very Good"
                              : +submission.evaluation.qualityValue >= 0.75
                              ? "Good"
                              : +submission.evaluation.qualityValue >= 0.6
                              ? "Fair"
                              : "Poor"}{" "}
                          </span>
                        </span>
                      )}

                      {!submission.evaluation.accepted && (
                        <div className="pb-2">
                          <span className="float-left text-uppercase">
                            <span
                              className="badge badge-light mb-2 text-danger"
                              style={{ backgroundColor: "transparent" }}
                            >
                              {/* <i className="fas fa-times text-danger"></i>{" "} */}
                              Rejected
                            </span>
                          </span>
                        </div>
                      )}

                      <span className="float-right mt-n2 text-uppercase d-inline-flex mt-1 align-items-center">
                        {submission.evaluation.accepted && (
                          <>
                            <span
                              className="badge badge-light mr-2 text-primary"
                              style={{ backgroundColor: "transparent" }}
                            >
                              Score:{" "}
                            </span>
                            <span style={{ width: "150px" }} className="mr-1">
                              <CustomProgressLine
                                value={submission.evaluation.score}
                                height={16}
                              />
                            </span>
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                  <TextField
                    style={{ backgroundColor: "#f6f8fa" }}
                    fullWidth
                    size="small"
                    InputProps={{
                      readOnly: true,
                    }}
                    multiline
                    className="mt-2"
                    rows={3}
                    name="name"
                    autoFocus
                    label="Supervisor comment"
                    variant="outlined"
                    value={submission.evaluation.supervisorComment}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>

      <ConfirmationDialog
        confirmationDialog={confirmRemoveSubmission}
        message={`Are you sure you want to remove this submission?`}
        setConfirmationDialog={setConfirmRemoveSubmission}
        onYes={() =>
          removeSubmission(
            {
              submissionId: submission.id,
              assignmentId: assignment.assignmentId,
              contractId: assignment.contractId,
              itemId: assignment.id,
              itemTypeId: assignment.itemTypeId,
            },
            setConfirmRemoveSubmission
          )
        }
      />

      {!!viewSupportDoc && (
        <PreviewPdfUrl
          showDocument={viewSupportDoc}
          setShowDocument={setViewSupportDoc}
          document={{
            name: `View Supporting Document`,
            url:
              process.env.REACT_APP_FILES_URL +
              "/preview/" +
              submission.docReferenceId,
          }}
        />
      )}
    </>
  );
};
